import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Mail, Github, Loader2, X, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../lib/auth.jsx';
import { cn } from '../lib/cn.js';

export default function AuthModal({ open, onOpenChange }) {
  const { signInWithEmail, signInWithOAuth } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(null); // 'email' | 'github' | 'google'
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);

  function reset() {
    setEmail('');
    setBusy(null);
    setErr('');
    setSent(false);
  }

  async function handleEmail(e) {
    e.preventDefault();
    setErr('');
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setErr('邮箱格式不太对，再检查一下');
      return;
    }
    setBusy('email');
    try {
      await signInWithEmail(email.trim());
      setSent(true);
    } catch (ex) {
      setErr(ex?.message || String(ex) || '发送失败');
    } finally {
      setBusy(null);
    }
  }

  async function handleOAuth(provider) {
    setErr('');
    setBusy(provider);
    try {
      await signInWithOAuth(provider);
      // OAuth 会跳走，不会回到这里
    } catch (ex) {
      setErr(ex?.message || String(ex) || '跳转失败');
      setBusy(null);
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange?.(v);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2',
            'rounded-2xl border border-ink-200 bg-white p-6 shadow-2xl',
            'focus:outline-none data-[state=open]:animate-in data-[state=open]:zoom-in-95',
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold text-ink-900">
                登录 careerops
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-ink-500">
                登录后简历、评估记录会同步到云端，每日免费配额从 5 次提升到 30 次。
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-lg p-1.5 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                aria-label="关闭"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {sent ? (
            <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">登录链接已发送</span>
              </div>
              <p className="mt-2 text-sm text-emerald-700/90">
                打开邮箱 <span className="font-mono">{email}</span>，点击邮件里的「Confirm your email」按钮即可登录。
              </p>
              <button
                type="button"
                onClick={reset}
                className="mt-3 text-xs text-emerald-700 underline hover:text-emerald-900"
              >
                没收到？换个邮箱再试
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={handleEmail} className="mt-5 space-y-3">
                <label className="block">
                  <span className="text-xs font-medium text-ink-600">邮箱</span>
                  <div className="mt-1 flex items-center gap-2 rounded-lg border border-ink-200 bg-white focus-within:border-accent-400 focus-within:ring-2 focus-within:ring-accent-100">
                    <Mail className="ml-3 h-4 w-4 text-ink-400" />
                    <input
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="flex-1 bg-transparent py-2 text-sm placeholder-ink-400 focus:outline-none"
                      disabled={!!busy}
                    />
                  </div>
                </label>
                <button
                  type="submit"
                  disabled={!!busy}
                  className="btn-primary w-full justify-center disabled:opacity-50"
                >
                  {busy === 'email' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      发送登录链接…
                    </>
                  ) : (
                    <>使用邮箱登录</>
                  )}
                </button>
              </form>

              <div className="my-4 flex items-center gap-3 text-xs text-ink-400">
                <div className="h-px flex-1 bg-ink-200" />
                <span>或</span>
                <div className="h-px flex-1 bg-ink-200" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handleOAuth('github')}
                  disabled={!!busy}
                  className="btn-secondary justify-center disabled:opacity-50"
                >
                  {busy === 'github' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Github className="h-4 w-4" />
                  )}
                  GitHub
                </button>
                <button
                  type="button"
                  onClick={() => handleOAuth('google')}
                  disabled={!!busy}
                  className="btn-secondary justify-center disabled:opacity-50"
                >
                  {busy === 'google' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <GoogleIcon />
                  )}
                  Google
                </button>
              </div>

              {err && (
                <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {err}
                </div>
              )}

              <p className="mt-4 text-[11px] leading-relaxed text-ink-400">
                登录即表示同意：我们仅在评估请求时把 CV / JD 文本传给 LLM；评估记录、用量统计存在你账号下，可随时删除。
              </p>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
      <path fill="#EA4335" d="M12 11v3.6h5.1c-.2 1.4-1.6 4.1-5.1 4.1-3 0-5.5-2.5-5.5-5.7s2.5-5.7 5.5-5.7c1.7 0 2.9.7 3.5 1.3l2.4-2.3C16.4 4.7 14.4 4 12 4 7 4 3 8 3 13s4 9 9 9c5.2 0 8.7-3.7 8.7-8.9 0-.6-.1-1-.2-1.5H12z" />
    </svg>
  );
}
